import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { physioApi, bookingApi, getAuthSession } from '../services/api';
import SlotAvailabilityPicker from '../components/SlotAvailabilityPicker';
import '../styles/TherapistProfileDetail.css';

function TherapistProfileDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  // Extract query params (e.g. ?sessionType=single&default_mode=offline&action=book)
  const defaultMode = searchParams.get('default_mode') || searchParams.get('mode') || 'online';
  const initialAction = searchParams.get('action') || '';
  const urlDate = searchParams.get('date') || '';
  const urlTime = searchParams.get('time') || '';

  const bookingCardRef = useRef(null);

  // State
  const [therapist, setTherapist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Booking Widget State
  const [sessionMode, setSessionMode] = useState(defaultMode === 'offline' ? 'offline' : 'online'); // 'online' (Video), 'offline' (In-Clinic), 'audio' (Voice)
  const [sessionDuration, setSessionDuration] = useState(50);
  const [selectedSlot, setSelectedSlot] = useState(urlDate && urlTime ? { date: urlDate, startTime: urlTime } : null);
  const [notes, setNotes] = useState('');
  const [offlineAddress, setOfflineAddress] = useState('');

  // UI state
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(null);
  const [bookingError, setBookingError] = useState('');
  const [copyToast, setCopyToast] = useState(false);

  // Fetch therapist details
  useEffect(() => {
    const fetchTherapist = async () => {
      try {
        setLoading(true);
        setError(null);

        const targetId = id || searchParams.get('physioId') || searchParams.get('therapistId');

        // Fetch public therapist list or specific therapist
        const response = await physioApi.search();
        const list = response.profiles || response.therapists || response.users || [];

        let found = null;
        if (targetId) {
          found = list.find(p => (p._id || p.id) === targetId);
        } else if (list.length > 0) {
          found = list[0];
        }

        if (!found) {
          throw new Error('Therapist profile not found.');
        }

        const isPro = found.membership === 'pro';
        const expYears = Number(found.yearsOfExperience) || (isPro ? 8 : 4);
        const fee = Number(found.consultationFee) || 999;
        const dur = Number(found.consultationDuration) || 50;

        let concerns = [
          'Feeling anxious for no reason or having sudden panic',
          'Feeling low in mood and losing interest in daily life',
          'Inability to carry on day-to-day activities and focus',
          'Having repetitive distressing thoughts or intrusive fears',
          'Difficulty with interpersonal relationships and coping',
          'Excessive workplace stress and professional burnout',
          'Experiencing grief, loss, or major life transition',
          'Sleep difficulties, insomnia, and chronic fatigue'
        ];

        if (found.therapyFor && Array.isArray(found.therapyFor) && found.therapyFor.length > 0) {
          concerns = found.therapyFor;
        } else if (found.specialization) {
          if (found.specialization.includes(',')) {
            concerns = found.specialization.split(',').map(s => `Help with ${s.trim()}`);
          }
        }

        const formatted = {
          _id: found._id || found.id,
          name: found.name ? (found.name.startsWith('Dr.') ? found.name : `Dr. ${found.name}`) : 'Dr. Mental Health Specialist',
          rawName: found.name || 'Practitioner',
          regNo: found.regNo || `RCI-${Math.floor(10000 + Math.random() * 90000)}`,
          specialty: found.specialization || (isPro ? 'Senior Clinical Psychologist & Psychotherapist' : 'Licensed Counselor & Therapist'),
          qualification: found.education || (isPro ? 'M.B.B.S, M.D. (Psychiatry), MRCPsych (UK), PGDP' : 'M.A. Applied Clinical Psychology, RCI Registered'),
          experience: expYears,
          location: found.location || 'Mumbai & Bangalore, India (Online & In-Clinic)',
          languages: (found.languages && Array.isArray(found.languages) && found.languages.length > 0) ? found.languages : ['English', 'Hindi', 'Kannada', 'Marathi'],
          concerns: concerns,
          shortDescription: found.shortDescription || '',
          bio: found.bio || (found.shortDescription ? found.shortDescription : `Dr. ${found.name || 'Practitioner'} brings over ${expYears} years of clinical expertise in mental health. Trained extensively across top psychiatric institutions and psychological research centres, they specialize in evidence-based psychological assessments, cognitive behavioral interventions, and holistic psychotherapy. Their practice is distinguished by an empathetic, non-judgmental, and client-centered approach to psychological well-being.`),
          consultationFee: fee,
          consultationDuration: dur,
          durationFees: found.durationFees || {
            30: Math.round(fee * 0.65),
            50: fee,
            80: Math.round(fee * 1.5),
          },
          image: found.image || '/assets/images/team/team-1-1.jpg',
          isVIP: isPro,
          rating: (4.8 + (Math.random() * 0.2)).toFixed(1),
          reviewCount: 45 + (expYears * 8),
          availableSlots: Array.isArray(found.availableSlots) ? found.availableSlots.filter(s => !s.isBooked) : [],
          weeklySchedule: found.weeklySchedule || null,
        };

        setTherapist(formatted);
        setSessionDuration(dur);
        if (found.location) {
          setOfflineAddress(found.location);
        }

        // Fetch custom slots if available
        try {
          const slotRes = await physioApi.getPublicSlots(formatted._id);
          if (slotRes?.availableSlots && slotRes.availableSlots.length > 0) {
            setTherapist(prev => ({
              ...prev,
              availableSlots: slotRes.availableSlots.filter(s => !s.isBooked),
              weeklySchedule: slotRes.therapist?.weeklySchedule || prev.weeklySchedule,
              durationFees: slotRes.therapist?.durationFees || prev.durationFees,
            }));
          }
        } catch (slotErr) {
          console.log('Public slots note:', slotErr.message);
        }

      } catch (err) {
        console.error('Failed to load therapist profile:', err);
        setError(err.message || 'Failed to load practitioner profile.');
      } finally {
        setLoading(false);
      }
    };

    fetchTherapist();
  }, [id, location.search]);

  // If action=book or scrolled to book, focus booking card
  useEffect(() => {
    if (initialAction === 'book' && bookingCardRef.current) {
      setTimeout(() => {
        bookingCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, [initialAction, loading]);

  // Handle Share Profile
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${therapist?.name} - PsychMitra Expert`,
        text: `Consult with ${therapist?.name}, ${therapist?.specialty} on PsychMitra.`,
        url: window.location.href,
      }).catch(() => { });
    } else {
      navigator.clipboard?.writeText(window.location.href);
      setCopyToast(true);
      setTimeout(() => setCopyToast(false), 2500);
    }
  };

  // Calculate dynamic price based on duration
  const calculatePrice = (durationMins) => {
    if (!therapist) return 999;
    const dur = Number(durationMins) || 50;
    if (therapist.durationFees && therapist.durationFees[dur] !== undefined) {
      return Number(therapist.durationFees[dur]);
    }
    const baseFee = therapist.consultationFee || 999;
    if (dur === 30) return Math.round(baseFee * 0.65);
    if (dur === 80) return Math.round(baseFee * 1.5);
    return baseFee;
  };

  // Handle Booking Submission
  const handleProceedBooking = async () => {
    setBookingError('');
    setBookingSuccess(null);

    const session = getAuthSession();
    if (!session || !session.token) {
      // Prompt user to login with redirect back
      navigate('/login', {
        state: {
          therapistName: therapist?.rawName || therapist?.name,
          redirectAfterLogin: window.location.pathname + window.location.search,
        }
      });
      return;
    }

    if (!selectedSlot || !selectedSlot.date || !selectedSlot.startTime) {
      setBookingError('Please choose an available date and time slot from the calendar.');
      bookingCardRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    try {
      setIsSubmitting(true);
      const scheduledAt = new Date(`${selectedSlot.date}T${selectedSlot.startTime}:00`).toISOString();

      const bookingPayload = {
        physiotherapistProfileId: therapist._id,
        mode: sessionMode,
        scheduledAt,
        duration: sessionDuration,
        amount: calculatePrice(sessionDuration),
        offlineAddress: sessionMode === 'offline' ? (offlineAddress || therapist.location) : '',
        notes: notes.trim(),
      };

      const res = await bookingApi.create(bookingPayload);
      setBookingSuccess(res.booking || { message: 'Booking completed successfully' });

      // Navigate to Client Bookings / Confirmation after short delay
      setTimeout(() => {
        navigate('/client/bookings', {
          state: {
            newBookingId: res.booking?._id || res._id,
            successMessage: `🎉 Your session with ${therapist.name} has been booked for ${selectedSlot.date} at ${selectedSlot.startTime}!`,
          }
        });
      }, 1500);

    } catch (err) {
      console.error('Booking creation failed:', err);
      setBookingError(err.message || 'Unable to book session. Please choose another slot or try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="amaha-page-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ width: '45px', height: '45px', border: '4px solid #e2e8f0', borderTop: '4px solid #e76943', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px auto' }}></div>
          <p style={{ color: '#64748b', fontWeight: 600 }}>Loading practitioner profile & schedule...</p>
        </div>
      </div>
    );
  }

  if (error || !therapist) {
    return (
      <div className="amaha-page-wrapper" style={{ padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ maxWidth: '500px', margin: '0 auto', background: '#fff', padding: '40px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🧑‍⚕️</div>
          <h3 style={{ color: '#0f172a', fontWeight: 700, marginBottom: '8px' }}>Practitioner Not Found</h3>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>{error || 'We could not locate this therapist profile.'}</p>
          <Link to="/therapists" className="thm-btn" style={{ padding: '12px 28px', borderRadius: '10px', textDecoration: 'none' }}>
            ← Browse All Therapists
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="amaha-page-wrapper">
      {/* Toast Notification for Share */}
      {copyToast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#0f172a',
          color: '#ffffff',
          padding: '12px 20px',
          borderRadius: '10px',
          zIndex: 9999,
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          fontSize: '0.9rem',
          fontWeight: 600
        }}>
          ✓ Profile link copied to clipboard!
        </div>
      )}

      {/* Breadcrumb Navigation */}
      <div className="amaha-breadcrumb">
        <div className="container">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/therapists">Therapists</Link>
          <span>/</span>
          <span className="active-crumb">{therapist.name}</span>
        </div>
      </div>

      {/* Main 2-Column Content Container */}
      <div className="amaha-main-container">
        {/* ================================================================= */}
        {/* LEFT COLUMN: PRACTITIONER DOSSIER & CLINICAL DETAILS */}
        {/* ================================================================= */}
        <div className="amaha-left-content">
          {/* 1. Header Profile Card */}
          <div className="amaha-profile-card">
            <div className="amaha-profile-header-row">
              <div className="amaha-profile-intro">
                <div className="amaha-avatar-wrapper">
                  <img
                    src={therapist.image}
                    alt={therapist.name}
                    className="amaha-avatar-img"
                    onError={(e) => { e.target.src = '/assets/images/team/team-1-1.jpg'; }}
                  />
                  <span className="amaha-verified-badge-icon" title="Verified Practitioner">✓</span>
                </div>

                <div className="amaha-title-block">
                  <h1>
                    {therapist.name}
                    {therapist.isVIP ? (
                      <span className="amaha-pro-pill">💎 Pro Practitioner</span>
                    ) : (
                      <span className="amaha-free-pill">✓ Verified</span>
                    )}
                  </h1>
                  <div className="amaha-reg-no">Reg. No.: {therapist.regNo}</div>
                  <div className="amaha-specialty-subtitle">{therapist.specialty}</div>
                </div>
              </div>

              {/* Share Profile Button */}
              <button
                type="button"
                className="amaha-share-btn"
                onClick={handleShare}
                title="Share practitioner profile"
              >
                🔗
              </button>
            </div>

            {/* Key Metrics Row (Award, Experience, Rating) */}
            <div className="amaha-metrics-row">
              <div className="amaha-metric-item">
                <span className="metric-icon">🎓</span>
                <span>{therapist.qualification}</span>
              </div>

              <div className="amaha-metric-item">
                <span className="metric-icon">💼</span>
                <span>{therapist.experience}+ Years Experience</span>
              </div>

              <div className="amaha-metric-item">
                <span className="amaha-rating-pill">
                  ⭐ {therapist.rating} ({therapist.reviewCount} Sessions)
                </span>
              </div>
            </div>
          </div>

          {/* 2. Summary & About Doctor Section */}
          <div className="amaha-section-card">
            <h3 className="amaha-section-title">
              <span className="sec-icon">👨‍⚕️</span> About {therapist.name}
            </h3>
            <p className={`amaha-bio-text ${isBioExpanded ? '' : 'collapsed'}`}>
              {therapist.bio}
            </p>
            <button
              type="button"
              className="amaha-readmore-btn"
              onClick={() => setIsBioExpanded(!isBioExpanded)}
            >
              {isBioExpanded ? 'Show less ▲' : 'Read more ▼'}
            </button>
          </div>

          {/* 3. Concerns I can help with (Amaha Quote Box) */}
          <div className="amaha-section-card">
            <h3 className="amaha-section-title">
              <span className="sec-icon">💬</span> Concerns I can help with
            </h3>
            <div className="amaha-concerns-box">
              {therapist.concerns.map((concern, idx) => (
                <div key={idx} className="amaha-concern-item">
                  <span className="amaha-quote-symbol">“</span>
                  <span className="amaha-concern-text">{concern}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 4. I offer therapy & psychiatry for (Conditions Grid) */}
          <div className="amaha-section-card">
            <h3 className="amaha-section-title">
              <span className="sec-icon">🧠</span> I offer therapy & clinical care for
            </h3>
            <div className="amaha-conditions-grid">
              <div className="amaha-condition-pill"><span className="cond-icon">⚡</span> Anxiety disorders</div>
              <div className="amaha-condition-pill"><span className="cond-icon">🌊</span> Depression & Mood</div>
              <div className="amaha-condition-pill"><span className="cond-icon">🔄</span> OCD & Intrusive Fears</div>
              <div className="amaha-condition-pill"><span className="cond-icon">🎯</span> Adult ADHD & Focus</div>
              <div className="amaha-condition-pill"><span className="cond-icon">🌿</span> Trauma & PTSD</div>
              <div className="amaha-condition-pill"><span className="cond-icon">👥</span> Relationship Difficulties</div>
              <div className="amaha-condition-pill"><span className="cond-icon">🔥</span> Burnout & Work Stress</div>
              <div className="amaha-condition-pill"><span className="cond-icon">🛌</span> Sleep & Insomnia</div>
            </div>
          </div>

          {/* 5. Languages I Speak */}
          <div className="amaha-section-card">
            <h3 className="amaha-section-title">
              <span className="sec-icon">🗣️</span> Languages I speak
            </h3>
            <div className="amaha-languages-flex">
              {therapist.languages.map((lang, idx) => (
                <div key={idx} className="amaha-lang-chip">
                  <span>🌐</span> {lang}
                </div>
              ))}
            </div>
          </div>

          {/* 6. My Affiliations & Clinical Credentials */}
          <div className="amaha-section-card">
            <h3 className="amaha-section-title">
              <span className="sec-icon">📜</span> My Affiliations & Accreditations
            </h3>
            <div className="amaha-affiliations-list">
              <div className="amaha-affiliation-item">
                <span className="amaha-affil-icon">✓</span>
                <span>Lead Clinical Consultant & Psychotherapist at <strong>PsychMitra Health Network</strong> (2022 - Present)</span>
              </div>
              <div className="amaha-affiliation-item">
                <span className="amaha-affil-icon">✓</span>
                <span>Registered Member of the <strong>Rehabilitation Council of India (RCI)</strong></span>
              </div>
              <div className="amaha-affiliation-item">
                <span className="amaha-affil-icon">✓</span>
                <span>Active Member, <strong>Indian Association of Clinical Psychologists (IACP)</strong></span>
              </div>
            </div>
          </div>

          {/* 7. Practitioner FAQs */}
          <div className="amaha-section-card">
            <h3 className="amaha-section-title">
              <span className="sec-icon">❓</span> Frequently Asked Questions
            </h3>

            <div className="amaha-faq-item open">
              <div className="amaha-faq-question" onClick={() => setOpenFaqIndex(openFaqIndex === 0 ? -1 : 0)}>
                <span>Why did you choose to become a mental health practitioner?</span>
                <span>{openFaqIndex === 0 ? '−' : '+'}</span>
              </div>
              {openFaqIndex === 0 && (
                <div className="amaha-faq-answer">
                  From an early age, I was deeply drawn to human behaviour, empathy, and psychological resilience. Clinical psychiatry and psychotherapy provide the ideal union of scientific medicine and deep emotional healing to empower clients toward thriving lives.
                </div>
              )}
            </div>

            <div className="amaha-faq-item">
              <div className="amaha-faq-question" onClick={() => setOpenFaqIndex(openFaqIndex === 1 ? -1 : 1)}>
                <span>What can I expect in my first consultation session?</span>
                <span>{openFaqIndex === 1 ? '−' : '+'}</span>
              </div>
              {openFaqIndex === 1 && (
                <div className="amaha-faq-answer">
                  Your first 50-minute session is a safe, completely non-judgmental space. We will discuss your current challenges, personal history, and what you wish to achieve, designing a personalized step-by-step roadmap together.
                </div>
              )}
            </div>

            <div className="amaha-faq-item">
              <div className="amaha-faq-question" onClick={() => setOpenFaqIndex(openFaqIndex === 2 ? -1 : 2)}>
                <span>Are our therapy sessions and records completely confidential?</span>
                <span>{openFaqIndex === 2 ? '−' : '+'}</span>
              </div>
              {openFaqIndex === 2 && (
                <div className="amaha-faq-answer">
                  Yes, 100%. All consultations and clinical records adhere to strict medical ethics, HIPAA, and Indian data protection guidelines. Nothing is ever shared without your explicit consent.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ================================================================= */}
        {/* RIGHT COLUMN: AMAHA STICKY BOOKING CARD */}
        {/* ================================================================= */}
        <div className="amaha-right-content" ref={bookingCardRef}>
          <div className="amaha-booking-card">
            <h3 className="amaha-booking-title">
              What type of session would you like?
            </h3>

            {/* Session Mode Selector (Video / In-Clinic / Audio) */}
            <div className="amaha-mode-grid">
              <button
                type="button"
                className={`amaha-mode-btn ${sessionMode === 'online' ? 'selected' : ''}`}
                onClick={() => setSessionMode('online')}
              >
                <span className="amaha-mode-icon">💻</span>
                <span className="amaha-mode-label">Video Call</span>
              </button>

              <button
                type="button"
                className={`amaha-mode-btn ${sessionMode === 'offline' ? 'selected' : ''}`}
                onClick={() => setSessionMode('offline')}
              >
                <span className="amaha-mode-icon">🏥</span>
                <span className="amaha-mode-label">In-Clinic</span>
              </button>

              <button
                type="button"
                className={`amaha-mode-btn ${sessionMode === 'audio' ? 'selected' : ''}`}
                onClick={() => setSessionMode('audio')}
              >
                <span className="amaha-mode-icon">📞</span>
                <span className="amaha-mode-label">Audio Call</span>
              </button>
            </div>

            {/* Session Duration Selector */}
            <div>
              <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>
                Session Duration
              </div>
              <div className="amaha-duration-row">
                <button
                  type="button"
                  className={`amaha-duration-pill ${sessionDuration === 30 ? 'selected' : ''}`}
                  onClick={() => setSessionDuration(30)}
                >
                  <div className="amaha-duration-time">30 Mins</div>
                  <div className="amaha-duration-price">₹{calculatePrice(30)}</div>
                </button>

                <button
                  type="button"
                  className={`amaha-duration-pill ${sessionDuration === 50 ? 'selected' : ''}`}
                  onClick={() => setSessionDuration(50)}
                >
                  <div className="amaha-duration-time">50 Mins</div>
                  <div className="amaha-duration-price">₹{calculatePrice(50)}</div>
                </button>

                <button
                  type="button"
                  className={`amaha-duration-pill ${sessionDuration === 80 ? 'selected' : ''}`}
                  onClick={() => setSessionDuration(80)}
                >
                  <div className="amaha-duration-time">80 Mins</div>
                  <div className="amaha-duration-price">₹{calculatePrice(80)}</div>
                </button>
              </div>
            </div>

            {/* In-Clinic Location details if offline mode */}
            {sessionMode === 'offline' && (
              <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '10px', fontSize: '0.85rem', border: '1px solid #e2e8f0' }}>
                <span style={{ fontWeight: 700, color: '#1e293b' }}>📍 Clinic Location:</span>
                <div style={{ color: '#475569', marginTop: '4px' }}>{therapist.location}</div>
              </div>
            )}

            {/* Interactive Amaha-style Date & Time Slot Picker */}
            <div className="amaha-slots-container">
              <SlotAvailabilityPicker
                slots={therapist.availableSlots || []}
                weeklySchedule={therapist.weeklySchedule || null}
                consultationDuration={sessionDuration}
                durationFee={calculatePrice(sessionDuration)}
                selectedSlot={selectedSlot}
                onSelectSlot={(slot) => setSelectedSlot(slot)}
                title="Check available slots"
                embedded={true}
              />
            </div>

            {/* Selected Slot Summary Indicator */}
            {selectedSlot && selectedSlot.date && selectedSlot.startTime && (
              <div style={{
                background: '#ecfdf5',
                border: '1.5px solid #a7f3d0',
                borderRadius: '10px',
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '0.88rem',
                color: '#065f46',
                fontWeight: 600
              }}>
                <div>
                  📅 Selected: <strong>{selectedSlot.date}</strong> at <strong>{selectedSlot.startTime}</strong>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedSlot(null)}
                  style={{ background: 'none', border: 'none', color: '#065f46', cursor: 'pointer', fontWeight: 700 }}
                >
                  ✕
                </button>
              </div>
            )}

            {/* Reason for Visit / Note */}
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '6px', display: 'block' }}>
                What's bringing you here? (Optional):
              </label>
              <textarea
                className="amaha-notes-field"
                rows="2"
                placeholder="Share anything you'd like the practitioner to know beforehand..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            {/* Error Message */}
            {bookingError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '0.88rem' }}>
                ⚠️ {bookingError}
              </div>
            )}

            {/* Success Message */}
            {bookingSuccess && (
              <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', padding: '12px 14px', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600 }}>
                ✓ Appointment confirmed! Redirecting to your dashboard...
              </div>
            )}

            {/* Pricing Summary */}
            <div className="amaha-price-summary">
              <div>
                <div className="amaha-price-label">Consultation Fee ({sessionDuration} mins)</div>
                <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 600 }}>✓ All taxes included</div>
              </div>
              <div className="amaha-price-amount">
                ₹{calculatePrice(sessionDuration)}
              </div>
            </div>

            {/* Big Proceed CTA Button */}
            <button
              type="button"
              className="amaha-proceed-btn"
              disabled={isSubmitting || !selectedSlot}
              onClick={handleProceedBooking}
            >
              {isSubmitting ? 'Booking Session...' : selectedSlot ? 'Proceed to Book →' : 'Select a Slot to Proceed'}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Booking Footer Bar */}
      <div className="amaha-mobile-booking-bar">
        <div className="amaha-mobile-bar-price">
          <span>Fee ({sessionDuration} mins)</span>
          <strong>₹{calculatePrice(sessionDuration)}</strong>
        </div>
        <button
          type="button"
          className="amaha-mobile-bar-btn"
          onClick={() => {
            bookingCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        >
          {selectedSlot ? 'Book Selected Slot' : '📅 Book Session'}
        </button>
      </div>
    </div>
  );
}

export default TherapistProfileDetailPage;
